import { highLevelMetrics } from 'api/CampaignIndexAPI';
import { EmbeddedMessage, MessageMedium, WhatsApp } from 'constants/messageMediums';
import { CampaignRetargetIconWithTooltip as NBARetargetIconWithTooltip } from 'modules/ai/nba/components/campaigns/CampaignRetargetIconWithTooltip';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Link } from 'ui/links';
import { Skeleton } from 'ui/skeleton';
import { colors, spacing, caption } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import {
  isRecurring,
  isTriggeredByApi,
  isTriggeredByEligibility,
  isTriggeredByWorkflow,
} from 'utils/campaigns';
import { TableCell } from '../CampaignIndexTable.styles';
import { useCampaignQueryContext } from '../campaignFolders/contexts/CampaignQueryContext';
import { selectCampaignMetrics, selectCampaignMetricsLoading } from '../selectors';
import { Campaign, CampaignList, Workflow } from '../types/Campaign';
import { parseDeliveryMetricPercentage, shouldDisplayCampaignMetrics } from '../utils';

const CaptionText = styled.div`
  ${caption};
  color: ${colors.textSecondary};
`;

const StyledCaptionTextWithIcon = styled(CaptionText)`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

const TotalText = styled.div`
  ${caption};
  color: ${colors.textPrimary};
`;

type Props = {
  campaign: Campaign;
};

const isTriggered = (campaign: Campaign) =>
  isTriggeredByWorkflow(campaign) || isTriggeredByApi(campaign);

const checkListsSuffix = (lists: CampaignList[]) => (lists.length > 1 ? 'lists' : 'list');

const VIEW_ANALYTICS = 'View analytics';

const generateKPI = (
  id: Campaign['id'],
  messageMedium: MessageMedium,
  campaignTotals: any = {},
  isCampaignNBAEnabled: boolean,
) => {
  const totalDelivered = campaignTotals[highLevelMetrics.totalDelivered.value] || 0;
  const totalSent = campaignTotals[highLevelMetrics.totalSent.value] || 0;
  const analyticsUrl = `/analytics/campaignPerformance?campaignId=${id}`;

  switch (messageMedium) {
    case MessageMedium.Email:
    case MessageMedium.Push:
    case MessageMedium.SMS:
      return (
        <>
          <CaptionText data-test="campaign-delivery-cell-kpi">
            {`${
              !totalSent ? 0 : parseDeliveryMetricPercentage(totalDelivered / totalSent)
            } delivered `}
          </CaptionText>
          <StyledCaptionTextWithIcon>
            <Link useReactRouter variant="monochrome" size="caption" href={analyticsUrl}>
              {VIEW_ANALYTICS}
            </Link>
            {isCampaignNBAEnabled && <NBARetargetIconWithTooltip id={id} />}
          </StyledCaptionTextWithIcon>
        </>
      );
    case MessageMedium.InApp:
    case MessageMedium.WebPush:
    case EmbeddedMessage:
      return (
        <StyledCaptionTextWithIcon>
          <Link useReactRouter variant="monochrome" size="caption" href={analyticsUrl}>
            {VIEW_ANALYTICS}
          </Link>
          {isCampaignNBAEnabled && <NBARetargetIconWithTooltip id={id} />}
        </StyledCaptionTextWithIcon>
      );
    case WhatsApp:
      return (
        <>
          <CaptionText data-test="campaign-delivery-cell-kpi">
            {`${
              !totalSent ? 0 : parseDeliveryMetricPercentage(totalDelivered / totalSent)
            } delivered `}
          </CaptionText>
          <StyledCaptionTextWithIcon>
            <Link useReactRouter variant="monochrome" size="caption" href={analyticsUrl}>
              {VIEW_ANALYTICS}
            </Link>
          </StyledCaptionTextWithIcon>
        </>
      );
    default:
      return null;
  }
};

const renderLoadingOrFailed = (
  id: Campaign['id'],
  isLoading: boolean,
  isCampaignNBAEnabled: boolean,
) => {
  const analyticsUrl = `/analytics/campaignPerformance?campaignId=${id}`;
  return isLoading ? (
    <Skeleton />
  ) : (
    <div>
      <CaptionText data-test="campaign-delivery-cell-metrics-failure">
        Metrics not loading
      </CaptionText>
      <StyledCaptionTextWithIcon>
        <Link
          useReactRouter
          variant="monochrome"
          size="caption"
          href={analyticsUrl}
          data-test="campaign-delivery-cell-metrics-failure-link"
        >
          {VIEW_ANALYTICS}
        </Link>
        {isCampaignNBAEnabled && <NBARetargetIconWithTooltip id={id} />}
      </StyledCaptionTextWithIcon>
    </div>
  );
};

/**
 * Campaign is running, recurring, or finished -> display the total sent or triggered so far
 * Hasn't started running and is triggered -> display "Workflow contacts" or "Triggered contacts"
 * Hasn't started running and isn't triggered -> display list and suppression list totals with tooltips
 */
const generateCellContent = (
  campaign: Campaign,
  campaignTotals: any,
  isLoading: boolean,
  isCampaignNBAEnabled: boolean,
) => {
  const { id, lists, suppressionLists } = campaign;
  const totals = campaignTotals[id] || {};
  const isDefined = totals[highLevelMetrics.totalSent.value] !== undefined;

  if (shouldDisplayCampaignMetrics(campaign)) {
    const metricKey = isTriggeredByEligibility(campaign)
      ? highLevelMetrics.totalRetrieved.value
      : highLevelMetrics.totalSent.value;
    const actionLabel =
      (isTriggeredByEligibility(campaign) && 'retrieved') ||
      (isTriggered(campaign) ? 'triggered' : 'sent');
    return isDefined ? (
      <div>
        <TotalText data-test="campaign-delivery-cell-total">
          {`${totals[metricKey]?.toLocaleString() || 0} Total ${actionLabel}`}
        </TotalText>
        {generateKPI(id, campaign.messageMedium, totals, isCampaignNBAEnabled)}
      </div>
    ) : (
      renderLoadingOrFailed(id, isLoading, isCampaignNBAEnabled)
    );
  }
  if (isTriggeredByApi(campaign)) {
    return <CaptionText>Triggered contacts</CaptionText>;
  }
  if (isTriggeredByWorkflow(campaign)) {
    const workflow = campaign.workflow as Workflow;
    return (
      <CaptionText data-test="campaign-delivery-workflow-contacts">
        <Tooltip isUnderlined tooltipContent={`${workflow.name} (${workflow.id})`}>
          Journey
        </Tooltip>{' '}
        contacts
      </CaptionText>
    );
  }

  const CaptionContent = () => {
    if (isRecurring(campaign)) return <>Sending to </>;
    if (isTriggeredByEligibility(campaign)) return <>Display to </>;
    return <>Send to </>;
  };

  // Blast campaign
  return (
    <div>
      {lists?.length > 0 ? (
        <CaptionText data-test="campaign-delivery-cell-list-totals">
          <CaptionContent />
          <Tooltip
            isUnderlined
            tooltipContent={
              <div>
                {lists.map((list) => (
                  <div key={list.id}>{`${list.name} (${list.id})`}</div>
                ))}
              </div>
            }
          >
            {`${lists.length} ${checkListsSuffix(lists)}`}
          </Tooltip>
        </CaptionText>
      ) : (
        <CaptionText data-test="campaign-delivery-cell-no-lists">Not set</CaptionText>
      )}
      {suppressionLists?.length > 0 && (
        <CaptionText data-test="campaign-delivery-cell-suppression-totals">
          Suppress{' '}
          <Tooltip
            isUnderlined
            tooltipContent={
              <div>
                {suppressionLists.map((list) => (
                  <div key={list.id}>{`${list.name} (${list.id})`}</div>
                ))}
              </div>
            }
          >
            {`${suppressionLists.length} ${checkListsSuffix(suppressionLists)}`}
          </Tooltip>
        </CaptionText>
      )}
    </div>
  );
};

export const CampaignDeliveryTableCell = ({ campaign }: Props) => {
  const campaignTotals = useSelector(selectCampaignMetrics);
  const campaignMetricsLoading = useSelector(selectCampaignMetricsLoading);
  const { nbaEnabledCampaignIds } = useCampaignQueryContext();
  const isCampaignNBAEnabled = nbaEnabledCampaignIds?.includes(campaign.id);
  return (
    <TableCell data-test="campaign-index-delivery-cell">
      {generateCellContent(campaign, campaignTotals, campaignMetricsLoading, isCampaignNBAEnabled)}
    </TableCell>
  );
};
